/**
 * `<StatusEditPopover>` — edit-in-place popover for the status-lozenge
 * atom. Mounted by `<StatusLozengeView>` when the chip is clicked.
 *
 * Layout (~260px):
 *
 *   ┌─ Color ──────────────────────────────────────┐
 *   │ ● ● ● ● ● ●                                   │
 *   │ ┌─────────────────────────────────────────┐  │
 *   │ │ [text input — chip label]               │  │
 *   │ └─────────────────────────────────────────┘  │
 *   └───────────────────────────────────────────────┘
 *
 * Positioning is absolute under the chip (the parent NodeView mounts
 * the popover as a sibling). Dismiss on outside-click / Escape /
 * Enter-while-text-focused.
 *
 * ARIA: the color row is a `radiogroup` (same shape as the custom-panel
 * picker's swatches); the text input has a label resolved from the wiki
 * catalog.
 */

import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { STATUS_COLORS, STATUS_COLOR_SWATCH_HEX, type StatusColor } from './status-kinds'

export interface StatusEditPopoverProps {
  color: StatusColor
  text: string
  onChange: (next: { color?: StatusColor; text?: string }) => void
  /** Caller closes the popover on outside click / Esc. */
  onClose: () => void
}

export function StatusEditPopover({ color, text, onChange, onClose }: StatusEditPopoverProps) {
  const { t } = useTranslation('wiki')
  const rootRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Auto-focus the text input on mount so a user can immediately type
  // a chip label. The color row gets the visual primary affordance but
  // text editing is the more common edit.
  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  // Close on outside-click + Escape. Same shape as `<IconPickerButton>`
  // in `CustomPanelBubbleMenu.tsx`.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return
      if (rootRef.current.contains(e.target as Node)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const onTextKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        onClose()
      }
    },
    [onClose],
  )

  const colorLabel = t('statusLozenge.colorLabel', { defaultValue: 'Color' })
  const textLabel = t('statusLozenge.textLabel', { defaultValue: 'Label' })
  const textPlaceholder = t('statusLozenge.textPlaceholder', { defaultValue: 'Status label' })

  return (
    <div
      ref={rootRef}
      contentEditable={false}
      // Stop ProseMirror from interpreting clicks inside the popover as
      // a selection change on the surrounding doc.
      onMouseDown={(e) => e.stopPropagation()}
      className="absolute z-50 mt-1 start-0 w-64 rounded-md border border-border-strong bg-bg-elevated p-3 shadow-lg"
    >
      <div className="flex flex-col gap-3">
        <div>
          <span
            id="status-color-label"
            className="block pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle"
          >
            {colorLabel}
          </span>
          <div
            role="radiogroup"
            aria-labelledby="status-color-label"
            className="flex items-center gap-1.5"
          >
            {STATUS_COLORS.map((slug) => {
              const isActive = slug === color
              const colorName = t(`statusLozenge.colors.${slug}`, { defaultValue: slug })
              return (
                <button
                  key={slug}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  aria-label={colorName}
                  onMouseDown={(e) => {
                    // mousedown + preventDefault so the input doesn't
                    // blur on click (which would close the popover).
                    e.preventDefault()
                    onChange({ color: slug })
                  }}
                  className={`h-6 w-6 rounded-full transition focus-visible:outline-none ${
                    isActive
                      ? 'ring-2 ring-accent ring-offset-2 ring-offset-bg-elevated'
                      : 'ring-1 ring-border'
                  }`}
                  style={{ backgroundColor: STATUS_COLOR_SWATCH_HEX[slug] }}
                  title={colorName}
                />
              )
            })}
          </div>
        </div>

        <div>
          <label
            htmlFor="status-text-input"
            className="block pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle"
          >
            {textLabel}
          </label>
          <input
            id="status-text-input"
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => onChange({ text: e.target.value })}
            onKeyDown={onTextKeyDown}
            placeholder={textPlaceholder}
            className="w-full rounded border border-border bg-bg px-2 py-1 text-sm text-fg outline-none placeholder:text-fg-subtle focus-visible:border-accent"
          />
        </div>
      </div>
    </div>
  )
}
